import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { toast } from "sonner";

/**
 * Substrate picker used in the Master Job Card.
 *
 * Selecting a substrate calls onPick(substrateDoc) so the parent can
 * hydrate substrate_type, substrate_code, substrate_description, gsm and
 * manufactured_by from a single click. Search is server-side (debounced)
 * so the picker stays snappy even with hundreds of substrates.
 */
export default function SubstrateCombobox({ value, onPick, testId }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(value || null);
  const [adding, setAdding] = useState(null); // { code, substrate_type, description, gsm, manufacturer } while the dialog is open
  const timerRef = useRef(null);

  // A code that is not on the list yet: add it here, and it is picked straight away.
  const addNew = async () => {
    const code = (adding.code || "").trim().toUpperCase();
    if (!code || !(adding.substrate_type || "").trim()) { toast.error("Code and type are required"); return; }
    try {
      const { data } = await api.post("/substrates", { ...adding, code, gsm: adding.gsm === "" ? null : Number(adding.gsm) });
      setPicked(data); onPick(data); setAdding(null); setOpen(false); setQ("");
      toast.success(`Substrate ${code} added`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add the substrate"); }
  };

  // Keep local `picked` in sync with parent selection. Match by id when we
  // have one, otherwise by code (edit-mode synthesized refs have no id).
  useEffect(() => {
    if (!value) return;
    const same = (picked?.id && value.id === picked.id) || (!value.id && value.code === picked?.code);
    if (!same) setPicked(value);
  }, [value, picked?.id, picked?.code]);

  useEffect(() => {
    if (!open) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/substrates", { params: { q, limit: 100, active_only: true } });
        setRows(data);
      } finally { setLoading(false); }
    }, q ? 200 : 0);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [q, open]);

  const handleOpenChange = (o) => { setOpen(o); if (!o) setQ(""); };
  const label = picked?.code
    ? `${picked.code}${picked.description ? ` · ${picked.description}` : ""}`
    : "Pick substrate…";

  return (
    <>
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          data-testid={testId}
          className="h-9 w-full justify-between rounded-sm border-neutral-300 text-sm font-normal"
        >
          <span className={picked ? "" : "text-neutral-400"}>{label}</span>
          <ChevronsUpDown size={14} className="text-neutral-500 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-[--radix-popover-trigger-width] min-w-[360px]">
        <Command shouldFilter={false}>
          <CommandInput data-testid={`${testId}-input`} placeholder="Search code / description / manufacturer…" value={q} onValueChange={setQ} />
          <CommandList>
            {loading && <div className="px-3 py-2 text-xs text-neutral-500">Searching…</div>}
            {!loading && rows.length === 0 && <CommandEmpty>No substrate matches{q.trim() ? "" : ": type a code to search"}.</CommandEmpty>}
            <CommandGroup>
              {rows.map((s) => (
                <CommandItem
                  key={s.id}
                  value={s.id}
                  data-testid={`${testId}-option-${s.id}`}
                  onSelect={() => { setPicked(s); onPick(s); setOpen(false); setQ(""); }}
                >
                  <Check size={13} className={`mr-2 ${picked?.id === s.id ? "opacity-100" : "opacity-0"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      <span className="font-mono-num">{s.code}</span>
                      {s.description ? <span className="text-neutral-700 font-normal"> · {s.description}</span> : null}
                    </div>
                    <div className="text-[10px] text-neutral-500 truncate">
                      {s.substrate_type}{s.gsm ? ` · ${s.gsm} gsm` : ""}{s.manufacturer ? ` · ${s.manufacturer}` : ""}
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
            <div className="border-t border-neutral-200">
              <button type="button" data-testid={`${testId}-add`} onClick={() => { setAdding({ code: q.trim().toUpperCase(), substrate_type: "", description: "", gsm: "", manufacturer: "" }); setOpen(false); }}
                className="w-full text-left px-3 py-2 text-sm text-[#FF5A00] hover:bg-orange-50 flex items-center gap-1.5">
                <Plus size={13} /> {q.trim() ? `Add “${q.trim().toUpperCase()}” as a new substrate` : "Add a new substrate…"}
              </button>
            </div>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
    <Dialog open={!!adding} onOpenChange={(v) => !v && setAdding(null)}>
      {adding && (
        <DialogContent className="max-w-md rounded-sm" data-testid={`${testId}-add-dialog`}>
          <DialogHeader>
            <DialogTitle>New substrate</DialogTitle>
            <DialogDescription>Saved to Inventory → Substrates and picked for this job. GSM and manufacturer fill in on every job that uses it.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs"><span className="label-caps block mb-1">Code *</span>
              <Input data-testid="sub-new-code" value={adding.code} onChange={(e) => setAdding({ ...adding, code: e.target.value })} className="rounded-sm font-mono-num" /></label>
            <label className="text-xs"><span className="label-caps block mb-1">Type * (e.g. PP WHITE)</span>
              <Input data-testid="sub-new-type" value={adding.substrate_type} onChange={(e) => setAdding({ ...adding, substrate_type: e.target.value })} className="rounded-sm" /></label>
            <label className="text-xs col-span-2"><span className="label-caps block mb-1">Description</span>
              <Input value={adding.description} onChange={(e) => setAdding({ ...adding, description: e.target.value })} className="rounded-sm" /></label>
            <label className="text-xs"><span className="label-caps block mb-1">GSM</span>
              <Input type="number" value={adding.gsm} onChange={(e) => setAdding({ ...adding, gsm: e.target.value })} className="rounded-sm" /></label>
            <label className="text-xs"><span className="label-caps block mb-1">Manufacturer</span>
              <Input value={adding.manufacturer} onChange={(e) => setAdding({ ...adding, manufacturer: e.target.value })} className="rounded-sm" /></label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => setAdding(null)}>Cancel</Button>
            <Button type="button" data-testid="sub-new-save" className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]" onClick={addNew}>Add and pick</Button>
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
    </>
  );
}
